import { CircleCheck, CircleSlash, Clock, KeyRound, LockKeyhole, Pencil, PlugZap, Plus, RefreshCw, ShieldAlert, Timer, Trash2 } from '@orgus/ui/icons';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { SECRET_MIN_LENGTH, SECRET_NAME_RE } from '@orgus/protocol';
import type { LeaseState, SecretLease, SecretMeta } from '@orgus/protocol';
import { Badge, Button, ConfirmDialog, Dialog, EmptyState, ErrorState, IconButton, Input, Panel, REVEAL_GROUP, RelativeTime, RevealSlot, Select, Skeleton, cn, toUiError, toast } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { useServer } from '../server/server.ts';
import { lockedNext, masterKeyCopy } from './secrets-copy.ts';
import { deleteSecret, revokeLease, rotateDataKey, saveSecret, useSecrets } from './live.ts';
import { useProjects } from './LaunchSettingsView.tsx';

/** A lease's state in its row: icon plus label, plain. Only an active lease carries colour. */
const LEASE_META: Record<LeaseState, { ink: string; icon: ReactNode; label: string }> = {
  active: { ink: 'text-status-working-ink [&_svg]:text-status-working', icon: <CircleCheck />, label: 'Active' },
  revoked: { ink: 'text-fg-subtle', icon: <CircleSlash />, label: 'Revoked' },
  expired: { ink: 'text-fg-subtle', icon: <Timer />, label: 'Expired' },
};

interface SecretForm {
  name: string;
  value: string;
  scope: string;
  description: string;
}

function SecretEditor({ open, onOpenChange, editing }: { open: boolean; onOpenChange: (o: boolean) => void; editing: SecretMeta | null }) {
  const { list } = useProjects();
  const [form, setForm] = useState<SecretForm>({ name: '', value: '', scope: 'org', description: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open) return;
    setForm(editing ? { name: editing.name, value: '', scope: editing.scope.kind === 'org' ? 'org' : editing.scope.projectId, description: editing.description ?? '' } : { name: '', value: '', scope: 'org', description: '' });
    setError(null);
    setBusy(false);
  }, [open, editing]);
  const set = <K extends keyof SecretForm>(k: K, v: SecretForm[K]) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const name = form.name.trim().toUpperCase();
    if (!SECRET_NAME_RE.test(name)) return setError('The name is the environment variable the session gets: capitals, digits and underscores.');
    if (!editing && !form.value) return setError('A new secret needs a value.');
    if (form.value && form.value.length < SECRET_MIN_LENGTH) return setError(`The value must be at least ${SECRET_MIN_LENGTH} characters, so it can be masked without hiding ordinary text.`);
    setBusy(true);
    setError(null);
    try {
      const meta = await saveSecret(
        { name, scope: form.scope === 'org' ? { kind: 'org' } : { kind: 'project', projectId: form.scope }, description: form.description.trim(), ...(form.value ? { value: form.value } : {}) },
        editing?.id,
      );
      setForm((f) => ({ ...f, value: '' }));
      toast(editing ? 'Secret updated' : 'Secret stored', { tone: 'success', description: `Choose ${meta.name} in Launch settings to lease it to sessions.` });
      onOpenChange(false);
    } catch (e) {
      const u = toUiError(e);
      setError(`${u.what} ${u.why ?? ''} ${u.next ?? ''}`.replace(/\s+/g, ' ').trim());
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !busy && onOpenChange(o)}
      title={editing ? `Edit ${editing.name}` : 'New secret'}
      description="Values are write-only: nobody can read them back, including you."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void save()}>
            {editing ? 'Save secret' : 'Store secret'}
          </Button>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <Input label="Name" value={form.name} onChange={(e) => set('name', e.target.value.toUpperCase())} placeholder="NPM_TOKEN" hint="The environment variable the session gets." spellCheck={false} className="font-mono" />
        <Input
          label={editing ? 'New value (leave empty to keep the current one)' : 'Value'}
          type="password"
          autoComplete="new-password"
          value={form.value}
          onChange={(e) => set('value', e.target.value)}
          placeholder={editing ? `Version ${editing.valueVersion} is stored` : 'Paste the value'}
          spellCheck={false}
          className="font-mono"
        />
        <Select label="Who may lease it" value={form.scope} onValueChange={(v) => set('scope', v)} options={[{ value: 'org', label: 'Sessions in every project' }, ...list.map((p) => ({ value: p.id, label: `Sessions in ${p.name} only` }))]} />
        <Input label="Description (optional)" value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Read-only token for the private registry" />
        <button type="submit" hidden />
      </form>
      {error && (
        <p role="alert" className="mt-4 rounded-md bg-failed-soft px-3 py-2 text-sm text-failed">
          {error}
        </p>
      )}
    </Dialog>
  );
}

function LeaseRow({ l, onRevoke }: { l: SecretLease; onRevoke: () => void }) {
  const machines = useOrgus((s) => s.machines);
  const sessions = useOrgus((s) => s.sessions);
  const m = LEASE_META[l.state];
  return (
    <li className={cn(REVEAL_GROUP, 'flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm')}>
      <span className={cn('inline-flex w-24 shrink-0 items-center gap-1 [&_svg]:size-3.5', m.ink)}>
        {m.icon}
        {m.label}
      </span>
      <span className="w-48 shrink-0 truncate font-mono text-fg" title={l.names.join(', ')}>
        {l.names.join(', ')}
      </span>
      <span className="min-w-0 flex-1 truncate text-fg-muted">
        {l.sessionId ? (sessions[l.sessionId]?.title ?? `Session ${l.sessionId.slice(0, 8)}`) : 'Session starting'} on {machines[l.machineId]?.name ?? l.machineId}
      </span>
      <span className="shrink-0 text-fg-subtle">
        {l.state === 'active' ? (
          <>
            leased <RelativeTime ts={l.createdAt} />
          </>
        ) : (
          <span title={l.endReason}>
            {l.endReason ?? 'Ended'} <RelativeTime ts={l.endedAt ?? l.createdAt} />
          </span>
        )}
      </span>
      {l.state === 'active' && (
        <RevealSlot className="shrink-0">
          <Button size="sm" variant="ghost" onClick={onRevoke}>
            Revoke lease
          </Button>
        </RevealSlot>
      )}
    </li>
  );
}

/** Settings, Secrets: encrypted values leased to launched sessions, the leases and the keyring. */
export function SecretsView() {
  const { phase, secrets } = useSecrets();
  const { name } = useProjects();
  const loadError = useOrgus((s) => s.loadError);
  const server = useServer().kind;
  const [editor, setEditor] = useState<{ open: boolean; s: SecretMeta | null }>({ open: false, s: null });
  const [removing, setRemoving] = useState<SecretMeta | null>(null);
  const [revoking, setRevoking] = useState<SecretLease | null>(null);
  const [rotating, setRotating] = useState(false);

  if (phase === 'loading')
    return (
      <div className="flex flex-col gap-3" role="status" aria-label="Loading secrets">
        <Skeleton className="h-[90px]" />
        <Skeleton className="h-[200px]" />
        <Skeleton className="h-[160px]" />
      </div>
    );
  if (phase === 'error') return <ErrorState error={loadError ?? new Error('The hub could not be reached.')} />;
  if (phase === 'missing' || !secrets) return <EmptyState icon={<PlugZap />} title="Secrets are not available on this hub" description="Update Orgus on the hub machine, then restart it." />;

  const k = secrets.keyring;
  const active = secrets.leases.filter((l) => l.state === 'active');
  const activeFor = (s: SecretMeta) => active.filter((l) => l.secretIds.includes(s.id)).length;
  return (
    <div className="flex max-w-[960px] flex-col gap-6">
      {k.error ? (
        <ErrorState error={{ what: 'Secrets are locked.', why: k.error, next: lockedNext(server) }} />
      ) : (
        <Panel
          title="Encryption"
          icon={<LockKeyhole />}
          actions={
            <Button size="sm" variant="ghost" icon={<RefreshCw />} onClick={() => setRotating(true)}>
              Rotate data key
            </Button>
          }
        >
          <dl className="grid gap-3 text-sm lg:grid-cols-3">
            <div className="flex flex-col gap-0.5">
              <dt className="text-fg-subtle">Master key</dt>
              <dd className="text-fg">
                {masterKeyCopy(server, k.masterKeySource, k.keystore)} <span className="font-mono text-fg-subtle">{k.masterKeyId}</span>
              </dd>
            </div>
            <div className="flex flex-col gap-0.5">
              <dt className="text-fg-subtle">Data key</dt>
              <dd className="text-fg">
                Created <RelativeTime ts={k.dataKeyCreatedAt} />
                {k.dataKeyRotatedAt ? (
                  <>
                    , rotated <RelativeTime ts={k.dataKeyRotatedAt} />
                  </>
                ) : null}
              </dd>
            </div>
            <div className="flex flex-col gap-0.5">
              <dt className="text-fg-subtle">Values</dt>
              <dd className="text-fg">AES-256-GCM, write-only</dd>
            </div>
          </dl>
          {k.masterKeySource === 'file' && server !== 'cloud' && (
            <p className="mt-3 flex items-start gap-2 rounded-md bg-waiting-soft px-3 py-2 text-sm text-fg">
              <ShieldAlert className="mt-0.5 size-3.5 shrink-0 text-waiting" aria-hidden />
              <span>The master key is in a file beside the data: set ORGUS_SECRETS_KEY (32 random bytes, base64) or run the hub where an OS keystore works, then restart it.</span>
            </p>
          )}
        </Panel>
      )}

      <Panel
        title="Secrets"
        sub={`${secrets.secrets.length} stored`}
        icon={<KeyRound />}
        flush
        actions={
          <Button size="sm" variant="primary" icon={<Plus />} onClick={() => setEditor({ open: true, s: null })} disabled={!!k.error}>
            New secret
          </Button>
        }
      >
        {secrets.secrets.length === 0 ? (
          <EmptyState
            icon={<KeyRound />}
            title="No secrets yet"
            description="Store a token here, then choose it in Launch settings."
            action={
              <Button size="sm" variant="secondary" icon={<Plus />} onClick={() => setEditor({ open: true, s: null })} disabled={!!k.error}>
                New secret
              </Button>
            }
          />
        ) : (
          <ul className="flex flex-col divide-y divide-border-subtle" aria-label="Secrets">
            {secrets.secrets.map((s) => (
              <li key={s.id} className={cn(REVEAL_GROUP, 'flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5')}>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="truncate font-mono text-sm text-fg">{s.name}</span>
                  {s.description ? <span className="truncate text-sm text-fg-muted">{s.description}</span> : <span className="text-sm text-fg-subtle" aria-label="No description">–</span>}
                </div>
                <span className="hidden w-36 shrink-0 truncate text-sm text-fg-muted md:inline">{s.scope.kind === 'org' ? 'Every project' : (name(s.scope.projectId) ?? 'Unknown project')}</span>
                <span className="w-28 shrink-0 text-sm text-fg-muted" title={`Value version ${s.valueVersion}`}>
                  <Badge tone="neutral" variant="outline" icon={<LockKeyhole />}>
                    Value v{s.valueVersion}
                  </Badge>
                </span>
                <span className="w-36 shrink-0 text-sm text-fg-subtle">
                  {s.lastLeasedAt ? (
                    <>
                      Leased <RelativeTime ts={s.lastLeasedAt} />
                    </>
                  ) : (
                    'Never leased'
                  )}
                </span>
                {s.owner ? (
                  // A launch account's key (ADR 0071): changed only with its account.
                  <Link to="/settings/accounts" className="shrink-0 text-sm text-accent-text underline-offset-2 hover:underline">
                    In Agent accounts
                  </Link>
                ) : (
                  <RevealSlot className="flex shrink-0 gap-0.5">
                    <IconButton size="xs" label={`Edit ${s.name} or replace its value`} icon={<Pencil />} onClick={() => setEditor({ open: true, s })} disabled={!!k.error} />
                    <IconButton size="xs" label={`Delete ${s.name}`} icon={<Trash2 />} variant="danger" onClick={() => setRemoving(s)} />
                  </RevealSlot>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="border-t border-border-subtle px-4 py-2.5 text-sm text-fg-subtle">
          Leased to headless sessions as environment variables, never written to disk, from <Link to="/settings/launch" className="text-accent-text underline-offset-2 hover:underline">Launch settings</Link>.
        </p>
      </Panel>

      <Panel title="Leases" sub={active.length ? `${active.length} active` : 'none active'} icon={<Clock />} flush>
        {secrets.leases.length === 0 ? (
          <EmptyState icon={<Clock />} title="No leases yet" description="A lease appears when a session Orgus starts receives secrets, and ends when that session ends." />
        ) : (
          <ul className="flex flex-col divide-y divide-border-subtle" aria-label="Leases, active first">
            {secrets.leases.map((l) => (
              <LeaseRow key={l.id} l={l} onRevoke={() => setRevoking(l)} />
            ))}
          </ul>
        )}
      </Panel>

      <SecretEditor open={editor.open} editing={editor.s} onOpenChange={(o) => setEditor((e) => ({ ...e, open: o }))} />
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={removing ? `Delete secret ${removing.name}?` : 'Delete secret?'}
        description={removing ? `The encrypted value is removed from the hub and new sessions stop getting it.${activeFor(removing) ? ` ${activeFor(removing)} running ${activeFor(removing) === 1 ? 'session keeps' : 'sessions keep'} the value until they end.` : ''} This cannot be undone.` : ''}
        confirmLabel="Delete secret"
        onConfirm={async () => {
          if (removing) await deleteSecret(removing.id);
          toast('Secret deleted', { tone: 'neutral' });
        }}
      />
      <ConfirmDialog
        open={!!revoking}
        onOpenChange={(o) => !o && setRevoking(null)}
        title={revoking ? `Revoke the lease of ${revoking.names.join(', ')}?` : 'Revoke lease?'}
        description="A session that already started keeps the value until it ends; stop it to take the value away."
        confirmLabel="Revoke lease"
        onConfirm={async () => {
          if (revoking) await revokeLease(revoking.id);
          toast('Lease revoked', { tone: 'neutral' });
        }}
      />
      <ConfirmDialog
        open={rotating}
        onOpenChange={setRotating}
        title="Rotate the data key?"
        description={`All ${secrets.secrets.length} values are encrypted again with a new key; values and leases stay the same.`}
        confirmLabel="Rotate data key"
        tone="primary"
        onConfirm={async () => {
          await rotateDataKey();
          toast('Data key rotated', { tone: 'success' });
        }}
      />
    </div>
  );
}
